import {
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    ActivatedRoute,
    NavigationEnd,
    NavigationStart,
    Router
} from '@angular/router';
import { DeliveryStepsService } from './services/delivery-steps.service';
import { AddressModel } from '../shared/models/address.model';
import { IdentificationStepsService } from './services/identification-steps.service';
import { AuthenticatedUserRepositoryService } from './services/authenticated-user-repository.service';
import { ClientModel } from '../shared/models/client.model';
import { ZipCodeRangeModel } from '../shared/models/zip-code-range.model';
import { isNull } from 'util';
import { ResumeUpdaterService } from '../cart/services/cart-resume-updater.service';
import { PaymentService } from './services/payment.service';
import { Modal, OneButtonPreset } from 'ngx-modialog/plugins/bootstrap';
import { DialogRef } from 'ngx-modialog';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { Observable } from 'rxjs/Observable';
import { FacebookService } from '../shared/services/facebook.service';
import { OauthService } from '../shared/services/oauth.service';
import { CartRowsUpdaterService } from '../cart/services/cart-rows-updater.service';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs/Subscription';
import { of } from 'rxjs/observable/of';
import { switchMap } from 'rxjs/operators';
import { CaptchaService } from './services/captcha.service';
import { env } from '../app.env';
import { RecaptchaComponent } from 'ng-recaptcha';

@AutoUnsubscribe()
@Component({
    selector: 'payment',
    templateUrl: './payment.component.html',
    styleUrls: ['./payment.component.scss'],
    encapsulation: ViewEncapsulation.None

})
export class PaymentComponent implements OnInit, OnDestroy {
    @ViewChild('captchaRef') public captchaRef: RecaptchaComponent;

    public identificationReady = false;
    public identificationActive = true;
    public deliveryAddressReady = false;
    public deliveryAddressActive = false;
    public deliveryAddressPending = true;
    public deliveryFreightReady = false;
    public deliveryFreightActive = false;
    public deliveryFreightPending = true;
    public paymentReady = false;
    public paymentActive = false;
    public paymentPending = true;

    public identifiedUser: ClientModel = null;
    public selectedAddress: AddressModel = null;
    public selectedFreight: ZipCodeRangeModel = null;

    public hasGoogleConversionId = false;
    public googleConversionId = null;
    public loading = false;
    public items = [];
    public captchaEnabled = false;
    public captchaSiteKey = '';

    private payingDialog: DialogRef<OneButtonPreset>;

    deliveryStepsServiceSelectedAddress: Subscription;
    deliveryStepsServiceSelectingFreight: Subscription;
    deliveryStepsServiceSelectedFreight: Subscription;
    deliveryStepsServiceResetedSelectedAddress: Subscription;
    deliveryStepsServiceResetedSelectedFreight: Subscription;
    deliveryStepsServiceResetedFreights: Subscription;
    authenticatedUserServiceResetedUser: Subscription;
    authenticatedUserServiceChangedUser: Subscription;
    resumeUpdaterServiceValidatedCoupon: Subscription;
    paymentServicePaying: Subscription;
    paymentServicePaid: Subscription;
    paymentServiceRefused: Subscription;
    oauthInitiated: Subscription;
    routerEventsSubscribe: Subscription;
    cartRowsUpdaterServiceIndexed: Subscription;

    constructor(
        protected activatedRoute: ActivatedRoute,
        protected router: Router,
        protected identificationStepsService: IdentificationStepsService,
        protected authenticatedUserService: AuthenticatedUserRepositoryService,
        protected resumeUpdaterService: ResumeUpdaterService,
        protected deliveryStepsService: DeliveryStepsService,
        protected paymentService: PaymentService,
        protected translate: TranslateService,
        protected titleService: Title,
        protected modal: Modal,
        protected facebookService: FacebookService,
        protected oauth: OauthService,
        protected cartRowsUpdaterService: CartRowsUpdaterService,
        protected captchaService: CaptchaService
    ) {
        this.routerEventsSubscribe = this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this.loading = true;
            }
            if (event instanceof NavigationEnd) {
                this.loading = false;
                this.oauthInitiated = this.oauth.init();
            }
        });
        this.translate.get('PAYMENT.TITLE').subscribe((title: string) => {
            this.titleService.setTitle(title  + ' | ' + this.activatedRoute.snapshot.data.storeConfigurations.store_name);
        });
        this.captchaService.resolutionRequired$.subscribe(() => {
            this.executeCaptcha();
        });
    }

    ngOnInit() {
        this.cartRowsUpdaterServiceIndexed = this.cartRowsUpdaterService.indexed$
            .subscribe(items => this.items = items);
        this.deliveryStepsServiceSelectedAddress = this.deliveryStepsService.selectedAddress$
            .subscribe((address: AddressModel) => {
                this.selectedAddress = address;
            });
        this.deliveryStepsServiceSelectingFreight = this.deliveryStepsService.selectingFreight$.subscribe(() => {
            this.loading = true;
        });
        this.deliveryStepsServiceSelectedFreight = this.deliveryStepsService.selectedFreight$.subscribe((freight: ZipCodeRangeModel) => {
            this.selectedFreight = freight;
            this.loading = false;
        });
        this.deliveryStepsServiceResetedSelectedAddress = this.deliveryStepsService.resetedSelectedAddress$.subscribe(() => {
            this.selectedAddress = null;
            this.selectedFreight = null;
        });
        this.deliveryStepsServiceResetedSelectedFreight = this.deliveryStepsService.resetedSelectedFreight$.subscribe(() => {
            this.selectedFreight = null;
        });
        this.deliveryStepsServiceResetedFreights = this.deliveryStepsService.resetedFreights$.subscribe(() => {
            this.selectedFreight = null;
        });
        this.authenticatedUserServiceResetedUser = this.authenticatedUserService.resetedUser$.subscribe(() => {
            this.selectedAddress = null;
            this.selectedFreight = null;
        });
        this.authenticatedUserServiceChangedUser = this.authenticatedUserService.changedUser$.subscribe(() => {
            this.cartRowsUpdaterService.refresh().subscribe();
        });
        this.resumeUpdaterServiceValidatedCoupon = this.resumeUpdaterService.validatedCoupon$.subscribe(() => {
            this.cartRowsUpdaterService.refresh().subscribe();
        });
        this.paymentServicePaying = this.paymentService.paying$.subscribe(() => {
            this.translate.get([
                'PAYMENT.PAYMENT.PAYING_MODAL.TITLE',
                'PAYMENT.PAYMENT.PAYING_MODAL.BODY'
            ]).subscribe((translated: string) => {
                this.modal
                .alert()
                .titleHtml(
                    '<h3>' +
                    '<i class="fa fa-circle-o-notch fa-spin"></i> ' +
                    translated['PAYMENT.PAYMENT.PAYING_MODAL.TITLE'] +
                    '</h3>'
                )
                .keyboard(1)
                .body(translated['PAYMENT.PAYMENT.PAYING_MODAL.BODY'])
                .isBlocking(true)
                .okBtnClass('')
                .dialogClass('modal-dialog paying')
                .showClose(false)
                .open()
                .then(dialog => {
                    this.payingDialog = dialog;
                    this.router.events
                    .filter(event => event instanceof NavigationStart)
                    .subscribe(event => {
                        this.payingDialog.close();
                    });
                });
            });
        });
        this.paymentServicePaid = this.paymentService.paid$.subscribe(() => {
            this.payingDialog.close();
            this.router.navigate(['/checkout/completed']);

            // Alternative hide modal for IOS
            const modal = Array.from(document.querySelectorAll('modal-overlay')); 
            modal.forEach(el => el.classList.add('d-none'));
        });
        this.paymentServiceRefused = this.paymentService.refused$
            .subscribe((reason: string) => {
                this.payingDialog.close();
                this.showRejectedMessagePopup(reason).subscribe();
            });

        this.handleStoreConfigurations(this.activatedRoute.snapshot.data);
        this.handleRestoredItems(this.activatedRoute.snapshot.data);
        this.facebookService.trackInitiateCheckout(
            this.items.map(item => item.id),
            this.facebookService.CONTENT_TYPE_PRODUCT,
            this.items.map(item => item.categories.map(category => category.name).join(',')).join('/'),
            this.items.map(item => ({'id': item.id, 'quantity': item.quantity})),
            this.items.reduce((sum, item) => sum += parseInt(item.quantity, 10), 0),
            this.items.reduce((sum, item) => sum += parseInt(item.quantity, 10) * parseFloat(item.price), 0)
        );

        this.resetPreviousDefinedPaymentSteps();
    }
    ngOnDestroy() {
        // Overrided by AutoUnsubscribe
    }

    public handleStoreConfigurations(data) {
        const definedStoreConfigurations = data['storeConfigurations'] !== undefined;
        if (!definedStoreConfigurations) {
            return;
        }

        const storeConfigurations = data['storeConfigurations'];

        this.captchaEnabled = storeConfigurations['checkout-captcha-enabled'] === '1';
        if (this.captchaEnabled) {
            this.captchaSiteKey = env.recaptcha_site_key;
        }

        this.facebookService.configure(storeConfigurations);
        this.facebookService.trackPageView();

        const definedGoogleConversionId = storeConfigurations['google-conversion-id'] !== undefined
            && storeConfigurations['google-conversion-id'] !== null;
        if (!definedGoogleConversionId) {
            return;
        }

        this.hasGoogleConversionId = true;
        this.googleConversionId = storeConfigurations['google-conversion-id'];
    }
    public handleRestoredItems(data) {
        this.items = data.cartRestore || [];
        this.paymentService.setDeliverable(this.isDeliverable());
    }

    /**
     * This redirect current route to /cart.
     **/
    public backToCart() {
        if (this.identificationStepsService.wasIdentifiedUser()) {
            this.deliveryStepsService.reset();
        }
        this.router.navigate(
            ['/checkout/cart'],
            {
                queryParamsHandling: 'merge',
                replaceUrl: true
            }
        );
    }

    /**
     * identificationActivated event listener
     **/
    public onIdentificationActivated() {
        this.identificationReady = false;
        this.identificationActive = true;

        this.onDeliveryAddressPending();
    }
    /**
     * identificationReady event listener
     **/
    public onIdentificationReady(user) {
        this.identifiedUser = user;
        this.identificationActive = false;
        this.identificationReady = true;

        this.onDeliveryAddressActivated();
    }

    /**
     * deliveryAddressActivated event listener
     **/
    public onDeliveryAddressActivated() {
        this.deliveryAddressPending = false;
        this.deliveryAddressActive = true;
        this.deliveryAddressReady = false;

        this.onDeliveryFreightPending();
        if (!this.isDeliverable()) {
            this.onDeliveryAddressReady();
        }
    }
    /**
     * deliveryAddressReady event listener
     **/
    public onDeliveryAddressReady() {
        this.deliveryAddressPending = false;
        this.deliveryAddressActive = false;
        this.deliveryAddressReady = true;

        this.onDeliveryFreightActivated();
    }
    /**
     * deliveryAddressPending event listener
     **/
    public onDeliveryAddressPending() {
        this.deliveryAddressPending = true;
        this.deliveryAddressActive = false;
        this.deliveryAddressReady = false;

        this.onDeliveryFreightPending();
    }
    /**
     * Returns true if selectedAddress is not null.
     **/
    public hasSelectedAddress(): boolean {
        return !isNull(this.selectedAddress);
    }
    /**
     * Update selected address info.
     **/
    public onUpdatedSelectedAddress(address: AddressModel) {
        this.selectedAddress = address;
    }

    /**
     * deliveryFreightActivated event listener
     **/
    public onDeliveryFreightActivated() {
        this.deliveryFreightPending = false;
        this.deliveryFreightActive = true;
        this.deliveryFreightReady = false;

        this.onPaymentPending();
        if (!this.isDeliverable()) {
            this.onDeliveryFreightReady();
        }
    }
    /**
     * deliveryFreightReady event listener
     **/
    public onDeliveryFreightReady() {
        this.deliveryFreightPending = false;
        this.deliveryFreightActive = false;
        this.deliveryFreightReady = true;

        this.onPaymentActivated();
    }
    /**
     * deliveryFreightPending event listener
     **/
    public onDeliveryFreightPending() {
        this.deliveryFreightPending = true;
        this.deliveryFreightActive = false;
        this.deliveryFreightReady = false;

        this.onPaymentPending();
    }
    /**
     * Returns true if selectedFreight is not null.
     **/
    public hasSelectedFreight(): boolean {
        return !isNull(this.selectedFreight) || !this.isDeliverable();
    }

    /**
     * paymentReady event listener
     **/
    public onPaymentReady() {
        setTimeout(() => {
            this.paymentReady = true;
            this.paymentActive = false;
            this.paymentPending = false;
        });
    }
    /**
     * paymentActivated event listener
     **/
    public onPaymentActivated() {
        setTimeout(() => {
            this.paymentActive = true;
            this.paymentPending = false;
            this.paymentReady = false;
        });
    }
    /**
     * paymentPending event listener
     **/
    public onPaymentPending() {
        this.paymentActive = false;
        this.paymentPending = true;
        this.paymentReady = false;
    }

    /**
     * Cart is deliverable if at least one item is deliverable
     **/
    public isDeliverable() {
        return this.items.filter(cartItem => {
            return cartItem.attributes.deliverable;
        }).length > 0;
    }

    /**
     * Dispatch captcha resolved token.
     **/
    public captchaResolved(token) {
        this.captchaService.resolved(token);
    }
    /**
     * Execute captcha validation.
     **/
    public executeCaptcha() {
        this.captchaRef.execute();
    }

    /**
     * Show popup with rejected payment message
     **/
    private showRejectedMessagePopup(reason: string): Observable<any> {
        return new Observable(observable => {
            this.translate.get([
                'PAYMENT.PAYMENT.PAYMENT_REJECTED_TITLE',
                'PAYMENT.PAYMENT.PAYMENT_REJECTED_BUTTON_TEXT'
            ]).subscribe((translated: string) => {
                this.modal.alert()
                    .title(translated['PAYMENT.PAYMENT.PAYMENT_REJECTED_TITLE'])
                    .body(reason)
                    .okBtn(translated['PAYMENT.PAYMENT.PAYMENT_REJECTED_BUTTON_TEXT'])
                    .okBtnClass('btn btn-danger')
                    .isBlocking(true)
                    .showClose(true)
                    .open()
                    .then(dialog => {
                        observable.next(dialog);
                        observable.complete();
                        /**
                         * WORKAROUND TO SOLVE 'NGX-MODIALOG' ISSUE
                         * Issue: https://github.com/shlomiassaf/ngx-modialog/issues/424
                         * ngx-modialog adds a 'modal-body' class to <body> when modal is
                         * opened to prevent scrolling. The problem is that after closing
                         * the modal, the class isn't removed.
                         *
                         * Solution: add class .scrollable to <body>
                         */
                        const bodyEl = document.getElementsByTagName('body')[0];
                        if (!/scrollable/.test(bodyEl.className)) {
                            bodyEl.className += ' scrollable';
                        }
                    });
            }, (err: Response) => {
                observable.error(err);
            });
        });
    }

    /**
     * Cascading reset about previous defined steps.
     **/
    private resetPreviousDefinedPaymentSteps() {
        of({}).pipe(
            switchMap(() => {
                return this.resumeUpdaterService.clearPaymentMethodDiscount();
            }),
            switchMap(() => {
                return this.resumeUpdaterService.removeCouponCode();
            }),
            switchMap(() => {
                this.deliveryStepsService.resetSelectedFreight();
                return of();
            })
        ).subscribe();
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src2032928750/src/src/app/payment/payment.component.ts